/**
 * checkDeviceRequirements.js
 * --------------------------
 * Runtime gatekeeper that decides whether the client device is allowed to run
 * the camera workflow.
 *
 * Returns:
 *   true  -> device passes OS and camera checks
 *   false -> any requirement failed
 */
import { firebaseApp } from "./firebaseApp";
import { collection, getDocs, getFirestore } from "firebase/firestore";

function extractMajorVersion(userAgent, osToken) {
  const versionFragment = userAgent
    .substring(userAgent.indexOf(osToken) + osToken.length)
    .match(/\d+/);

  if (!versionFragment) return null;

  const parsed = parseInt(versionFragment[0], 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function capabilityMax(value) {
  if (typeof value === "number") return value;
  if (value && typeof value.max === "number") return value.max;
  return null;
}

function meetsCameraRequirements(capabilities) {
  const heightMax = capabilityMax(capabilities?.height);
  const widthMax = capabilityMax(capabilities?.width);
  const frameRateMax = capabilityMax(capabilities?.frameRate);

  const has720p =
    (heightMax != null && heightMax >= 720) ||
    (widthMax != null && widthMax >= 1280);
  const has30Fps = frameRateMax != null && frameRateMax >= 30;

  return has720p && has30Fps;
}

function supportsRecordingApis() {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined"
  );
}

export const checkDeviceRequirements = async (
  userAgent,
  { requireCameraAccess = false } = {},
) => {
  try {
    const db = getFirestore(firebaseApp);
    const collectionRef = collection(db, "device_requirements");
    const querySnapshot = await getDocs(collectionRef);

    let meetsOsRequirement = false;

    querySnapshot.forEach((snapshotDoc) => {
      if (!userAgent.includes(snapshotDoc.id)) return;

      const minimumOS = snapshotDoc.data()?.minimum_os;
      const osVersion = extractMajorVersion(userAgent, snapshotDoc.id);

      if (Number.isFinite(minimumOS) && osVersion != null && osVersion >= minimumOS) {
        meetsOsRequirement = true;
      }
    });

    if (!meetsOsRequirement) return false;
    if (!supportsRecordingApis()) return false;

    // Route-level compatibility checks must stay non-invasive: asking for camera
    // access before the user reaches the recording flow trains them to hit
    // "Don't Allow" on a screen that cannot yet explain why access is needed.
    // The pod owns the real permission request and recovery guidance.
    if (!requireCameraAccess) return true;

    // Ask for the front camera, because that is the one every recording workflow
    // uses — measuring the rear camera's resolution and frame rate answers the
    // wrong question, and on Android `{video: true}` is exactly the rear camera.
    //
    // It also stopped opening the rear camera moments before the recording pod
    // acquired its preview, which on a Realme phone left the rear camera the one
    // Chrome reached for and produced a recording pointing away from the
    // participant. `ideal` rather than `exact`: nothing here competes with it, so
    // it is enough to select the front camera, while `exact` would fail outright
    // on desktop webcams that report no facingMode and send those users to the
    // incompatible-device page.
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: "user" } },
    });

    try {
      const videoTrack = stream.getVideoTracks?.()[0];
      const capabilities = videoTrack?.getCapabilities?.();

      if (!capabilities) return false;
      return meetsCameraRequirements(capabilities);
    } finally {
      // Every track, not just the first video one: anything left running holds the
      // camera open against whatever acquires it next.
      stream.getTracks?.().forEach((track) => {
        try {
          track.stop();
        } catch {}
      });
    }
  } catch (error) {
    console.error("Error checking device requirements:", error);
    return false;
  }
};
